import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type TeamTemplateAccessJSON = z.infer<typeof TeamTemplateAccessSchema.TeamTemplateAccessJSONZ>;

export type TeamTemplateAccessParams = z.infer<typeof TeamTemplateAccessSchema.TeamTemplateAccessZ>;

export default class TeamTemplateAccessSchema {
  public static readonly TeamTemplateAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.TeamTemplateAccess),
    templateId: z.uuid().nullish(),
    accessLevel: z.enum(AccessLevel).nullish(),
  });

  public static readonly TeamTemplateAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.TeamTemplateAccess),
    templateId: z.instanceof(Guid).optional(),
    accessLevel: z.enum(AccessLevel).optional(),
  });

  public static validate(values: unknown): TeamTemplateAccessParams {
    return parseOrThrow(TeamTemplateAccessSchema.TeamTemplateAccessZ, values, {
      logError: true,
      schemaName: "TeamTemplateAccessSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): TeamTemplateAccessJSON {
    return parseOrThrow(TeamTemplateAccessSchema.TeamTemplateAccessJSONZ, values, {
      logError: true,
      schemaName: "TeamTemplateAccessSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is TeamTemplateAccessParams {
    try {
      TeamTemplateAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is TeamTemplateAccessJSON {
    try {
      TeamTemplateAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: TeamTemplateAccessJSON): TeamTemplateAccessParams {
    const parsed = parseOrThrow(TeamTemplateAccessSchema.TeamTemplateAccessJSONZ, data, {
      logError: true,
      schemaName: "TeamTemplateAccessSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return TeamTemplateAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: TeamTemplateAccessParams): TeamTemplateAccessJSON {
    const parsed = TeamTemplateAccessSchema.validate(notification);
    return TeamTemplateAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    TeamTemplateAccessSchema.TeamTemplateAccessJSONZ,
    TeamTemplateAccessSchema.TeamTemplateAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.TeamTemplateAccess as const,
        templateId: Guid.fromJSON(data.templateId) ?? undefined,
        accessLevel: data.accessLevel ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.TeamTemplateAccess as const,
        templateId: resource.templateId?.toJSON(),
        accessLevel: resource.accessLevel,
      }),
    }
  );
}
